import React from 'react';
import { Link } from 'react-router-dom';
import Portrait from '../brand/Portrait';
import type { OutreachCard } from '../../services/dataService';
import { dealHref, dealStatus, needsYou } from '../bearri/dealStatus';

/**
 * Deals at a glance, on the dashboard right under Your sponsors (the same people, further along):
 * where each conversation stands, and the few that need you — each one opens that deal in the
 * Bearri tab. The section heading above it carries the title and the "Open in Bearri" link. Real data only: every count
 * comes from the pipeline, and an empty pipeline says so. The words for a deal's state are the
 * Bearri tab's own (components/bearri/dealStatus), so the two never describe a deal two ways.
 */

/** Deals at a glance: four counts, then what needs you. */
export function DealsGlance({ cards, error = false }: { cards: OutreachCard[] | null; error?: boolean }) {
  const count = (...stages: OutreachCard['stage'][]) => (cards || []).filter(c => stages.includes(c.stage)).length;
  const won = (cards || []).filter(c => c.stage === 'won');
  const amounts = won.map(c => c.deal_amount).filter((a): a is number => typeof a === 'number');
  const needs = cards ? needsYou(cards) : [];
  return (
    <div className="br-sheet br-desk-card br-deals-glance" aria-busy={cards === null}>
      {error ? (
        /* a failed read is said as a failed read — never as an empty pipeline */
        <p className="br-ruled-empty">Couldn’t read your deals just now. <Link to="/app/bearri" className="br-inkline">Open Bearri</Link></p>
      ) : cards === null ? (
        <p className="br-ruled-empty br-ruled-empty--loading" role="status">Reading your deals…</p>
      ) : cards.length === 0 ? (
        <div className="br-deals-glance__empty">
          <p>Nothing in flight yet. Find a brand that fits, or write a pitch to one you already have in mind.</p>
          <div className="br-deals-glance__actions">
            <Link to="/app/bearri?scout=new" className="br-slab br-slab--blue br-slab--sm">Find brands</Link>
            <Link to="/app/pitch-writer" className="br-slab br-slab--white br-slab--sm">Write a pitch</Link>
          </div>
        </div>
      ) : (
        <div className="br-deals-glance__body">
          <dl className="br-deals-glance__counts">
            <div className={`br-deals-glance__count${count('replied') ? ' is-hot' : ''}`}><dt>Replied</dt><dd>{count('replied')}</dd></div>
            <div className="br-deals-glance__count"><dt>Awaiting</dt><dd>{count('awaiting', 'delivery_check')}</dd></div>
            <div className="br-deals-glance__count"><dt>Drafts</dt><dd>{count('drafted')}</dd></div>
            <div className="br-deals-glance__count is-money">
              <dt>Won</dt>
              <dd>{amounts.length ? `$${amounts.reduce((s, a) => s + a, 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : won.length}</dd>
            </div>
          </dl>
          {needs.length > 0 ? (
            <div className="br-deals-glance__needs">
              <p className="br-deals-glance__label">Needs you</p>
              <ul className="br-ruled">
                {needs.map(card => (
                  <li key={card.id}>
                    <Link to={dealHref(card.id)} className="br-deals-glance__item">
                      <Portrait name={card.brand_name} src={card.brand_logo} size="xs" mark />
                      <span className="br-deals-glance__copy">
                        <strong>{card.brand_name}</strong>
                        <span>{dealStatus(card).line}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="br-deals-glance__clear">Nothing needs you right now — every pitch is sent and nothing has gone quiet.</p>
          )}
        </div>
      )}
    </div>
  );
}
